import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, Trash2, Users, Filter, ExternalLink, Star, MessageCircle } from "lucide-react";

const TABS = [
  { key: "pending", label: "बाकी", cls: "bg-amber-500 text-white" },
  { key: "approved", label: "प्रकाशित", cls: "bg-emerald-600 text-white" },
  { key: "rejected", label: "रद्द", cls: "bg-slate-500 text-white" },
];

export default function ContributorAdminPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [savingPick, setSavingPick] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/contributor-submissions", { params: { status: tab } })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const doAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("पक्का हटाएँ?")) return;
        await api.delete(`/admin/contributor-submissions/${id}`);
        toast.success("हटा दिया गया");
      } else {
        await api.post(`/admin/contributor-submissions/${id}/${action}`);
        toast.success(action === "approve" ? "प्रकाशित कर दिया" : "अस्वीकार कर दिया");
      }
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const openReply = (item) => {
    setEditingId(item.id);
    setReplyText(item.editor_reply || "");
  };
  const closeReply = () => { setEditingId(null); setReplyText(""); };

  const savePick = async (id, pick, withReply = false) => {
    setSavingPick(true);
    try {
      const body = { pick };
      if (withReply) body.reply = replyText;
      await api.post(`/admin/contributor-submissions/${id}/editor-pick`, body);
      toast.success(pick ? "🌟 प्रधान संपादक की सिफ़ारिश जोड़ी" : "सिफ़ारिश हटाई");
      closeReply();
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSavingPick(false); }
  };

  return (
    <div data-testid="contributor-admin-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3">
            <Users className="w-7 h-7 text-[#EA580C]"/> पाठक योगदान
          </h1>
          <p className="text-sm text-slate-500 mt-1">Reader Contributor Wall · जनता की भेजी रिपोर्ट की समीक्षा करें</p>
        </div>
        <a href="/contribute" target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 text-slate-500 hover:text-[#0F172A]" data-testid="view-wall">
          <ExternalLink className="w-3.5 h-3.5"/> पब्लिक वॉल देखें
        </a>
      </div>

      <div className="flex items-center gap-2 mb-6" data-testid="tabs">
        <Filter className="w-4 h-4 text-slate-400"/>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition ${tab === t.key ? t.cls : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-500">कुल: <b className="text-slate-700">{items.length}</b></div>
      </div>

      {loading ? (
        <div className="text-slate-500 py-10 text-center">लोड हो रहा है…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <Users className="w-14 h-14 text-slate-300 mx-auto mb-3"/>
          <div className="font-bold text-slate-700">कोई सबमिशन नहीं</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((c) => (
            <div key={c.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`contrib-admin-${c.id}`}>
              {c.photo_url && (
                <a href={c.photo_url.startsWith("http") ? c.photo_url : `${API.replace(/\/api$/, "")}${c.photo_url}`} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-slate-100">
                  <img src={c.photo_url.startsWith("http") ? c.photo_url : `${API.replace(/\/api$/, "")}${c.photo_url}`} alt="" className="w-full h-full object-cover"/>
                </a>
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black text-xs">{c.name?.[0] || "?"}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-[#0F172A] truncate">{c.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {c.city ? `📍 ${c.city}` : ""} {c.phone ? ` · 📞 ${c.phone}` : ""}
                    </div>
                    <div className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleString("hi-IN")}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed max-h-40 overflow-y-auto whitespace-pre-wrap border-l-4 border-amber-200 pl-3">{c.text}</p>

                {c.editor_pick && (
                  <div className="mt-2 inline-flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-900 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest">
                    <Star className="w-3 h-3 fill-amber-500 text-amber-600"/> प्रधान संपादक की सिफ़ारिश
                  </div>
                )}
                {c.editor_reply && (
                  <div className="mt-2 bg-slate-50 border-l-4 border-amber-400 rounded p-2 text-xs text-slate-700">
                    <div className="text-[9px] uppercase tracking-widest text-amber-700 font-black mb-1">प्रधान संपादक का जवाब</div>
                    <div className="whitespace-pre-wrap italic">"{c.editor_reply}"</div>
                  </div>
                )}

                {editingId === c.id ? (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3">
                    <label className="text-[10px] uppercase tracking-widest font-black text-amber-800 mb-1 block">प्रधान संपादक का जवाब (600 अक्षर)</label>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={3}
                      maxLength={600}
                      placeholder="धन्यवाद! आपकी रिपोर्ट ज़रूर आगे रखेंगे…"
                      className="w-full text-sm rounded border border-amber-300 p-2 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                      data-testid={`reply-input-${c.id}`}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button onClick={() => savePick(c.id, true, true)} size="sm" disabled={savingPick} className="bg-amber-500 hover:bg-amber-600 text-white flex-1" data-testid={`save-reply-${c.id}`}>
                        <Star className="w-3.5 h-3.5 mr-1"/> सिफ़ारिश + जवाब सेव करें
                      </Button>
                      <Button onClick={closeReply} size="sm" variant="outline">रद्द</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {c.status !== "approved" && (
                      <Button onClick={() => doAction(c.id, "approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${c.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1"/> प्रकाशित
                      </Button>
                    )}
                    {c.status === "approved" && (
                      c.editor_pick ? (
                        <Button onClick={() => savePick(c.id, false)} size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`unpick-${c.id}`}>
                          <Star className="w-3.5 h-3.5 mr-1 fill-amber-500"/> सिफ़ारिश हटाएँ
                        </Button>
                      ) : (
                        <Button onClick={() => openReply(c)} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`pick-${c.id}`}>
                          <Star className="w-3.5 h-3.5 mr-1"/> सिफ़ारिश दें
                        </Button>
                      )
                    )}
                    {c.status === "approved" && (
                      <Button onClick={() => openReply(c)} size="sm" variant="outline" className="text-slate-700" data-testid={`edit-reply-${c.id}`}>
                        <MessageCircle className="w-3.5 h-3.5 mr-1"/> {c.editor_reply ? "जवाब बदलें" : "जवाब दें"}
                      </Button>
                    )}
                    {c.status !== "rejected" && (
                      <Button onClick={() => doAction(c.id, "reject")} size="sm" variant="outline" data-testid={`reject-${c.id}`}>
                        <X className="w-3.5 h-3.5 mr-1"/> रद्द
                      </Button>
                    )}
                    <Button onClick={() => doAction(c.id, "delete")} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 ml-auto" data-testid={`delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
